import { c as _c } from "react/compiler-runtime";
import { usePathname, useSearchParams, useRouter } from "next/navigation";
import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore, Suspense } from "react";
import { Skeleton } from "@/src/components/shadcn/ui/skeleton";
import { isServer } from "@/src/lib/runtime";
import { getSessionItem, setSessionItem } from "@/src/lib/session-storage";
import type { ParserMap, inferParserType } from "nuqs";
const TABLE_FILTERS_PREFIX = "table-filters:";
function getTableFiltersKey(path: string): string {
  return `${TABLE_FILTERS_PREFIX}${path}`;
}
const DASHBOARD_BASE_PATHS = ["/appointments", "/authorizations", "/files", "/inbox", "/leads", "/locations", "/orders", "/patients", "/providers", "/referrals", "/tasks", "/voice"] as const;
export function getBasePath(pathname: string): string {
  for (const base of DASHBOARD_BASE_PATHS) {
    if (pathname === base || pathname.startsWith(`${base}/`)) {
      return base;
    }
  }
  return pathname;
}
function isDashboardPath(pathname: string): boolean {
  return DASHBOARD_BASE_PATHS.some(base => pathname === base || pathname.startsWith(`${base}/`));
}
interface DataTableSearchContext {
  storedParamsByPath: Record<string, URLSearchParams>;
  storeSearchContext: () => void;
  setStoredParams: (path: string, params: URLSearchParams) => void;
}
const DataTableSearchContext = createContext<DataTableSearchContext>({
  storedParamsByPath: {},
  storeSearchContext: () => {},
  setStoredParams: () => {}
});
export function useDataTableSearchContext() {
  return useContext(DataTableSearchContext);
}
function QueryStringSessionSyncGate({
  children
}: PropsWithChildren) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const router = useRouter();
  const hasSeenNonEmptyByPathRef = useRef<Record<string, boolean>>({});
  const basePath = getBasePath(pathname);
  const hasParams = searchParams.toString().length > 0;
  const isListPage = isDashboardPath(pathname) && pathname === basePath;
  const storedQuery = isListPage && !isServer ? getSessionItem(getTableFiltersKey(basePath))?.trim() || null : null;
  const needsRestore = isListPage && !hasParams && !!storedQuery && !hasSeenNonEmptyByPathRef.current[basePath];
  useLayoutEffect(() => {
    if (isServer || !pathname || !isDashboardPath(pathname)) return;
    const basePath_0 = getBasePath(pathname);
    const qs = searchParams.toString();
    const key = getTableFiltersKey(basePath_0);
    if (pathname !== basePath_0) {
      delete hasSeenNonEmptyByPathRef.current[basePath_0];
      return;
    }
    if (qs.length > 0) {
      hasSeenNonEmptyByPathRef.current[basePath_0] = true;
      setSessionItem(key, qs);
      return;
    }
    if (hasSeenNonEmptyByPathRef.current[basePath_0]) {
      setSessionItem(key, "");
      return;
    }
    const s = getSessionItem(key);
    if (!s?.trim()) return;
    router.replace(`${basePath_0}?${s.trim()}`);
  }, [pathname, searchParams, router]);
  if (needsRestore) {
    return <Skeleton className="h-[50vh] w-full" />;
  }
  return <>{children}</>;
}
function DataTableSearchParamsSync(t0) {
  const $ = _c(5);
  const {
    setStoredParamsByPath
  } = t0;
  const pathname = usePathname();
  const searchParams = useSearchParams();
  let t1;
  let t2;
  if ($[0] !== pathname || $[1] !== searchParams || $[2] !== setStoredParamsByPath) {
    t1 = () => {
      if (isServer || !pathname) {
        return;
      }
      const basePath = getBasePath(pathname);
      if (pathname !== basePath) {
        return;
      }
      const params = new URLSearchParams(searchParams.toString());
      setStoredParamsByPath(prev => {
        const currentParamsString = params.toString();
        const prevParamsString = prev[basePath]?.toString();
        if (currentParamsString === prevParamsString) {
          return prev;
        }
        return {
          ...prev,
          [basePath]: params
        };
      });
    };
    t2 = [pathname, searchParams, setStoredParamsByPath];
    $[0] = pathname;
    $[1] = searchParams;
    $[2] = setStoredParamsByPath;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  return null;
}
export function DataTableSearchProvider(t0) {
  const $ = _c(11);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {};
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [storedParamsByPath, setStoredParamsByPath] = useState(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      if (isServer) {
        return;
      }
      const {
        pathname
      } = window.location;
      const basePath = getBasePath(pathname);
      if (isDashboardPath(pathname) && pathname !== basePath) {
        return;
      }
      if (window.location.pathname !== basePath) {
        return;
      }
      setStoredParamsByPath(prev => ({
        ...prev,
        [basePath]: new URLSearchParams(window.location.search)
      }));
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const storeSearchContext = t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = (path, params) => {
      setSessionItem(getTableFiltersKey(path), params.toString());
      setStoredParamsByPath(prev_0 => ({
        ...prev_0,
        [path]: params
      }));
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const setStoredParams = t3;
  let t4;
  if ($[3] !== storedParamsByPath) {
    t4 = {
      storedParamsByPath,
      storeSearchContext,
      setStoredParams
    };
    $[3] = storedParamsByPath;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const ctx = t4;
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <DataTableSearchParamsSync setStoredParamsByPath={setStoredParamsByPath} />;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== children) {
    t6 = <Suspense fallback={null}><QueryStringSessionSyncGate>{t5}{children}</QueryStringSessionSyncGate></Suspense>;
    $[6] = children;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== ctx || $[9] !== t6) {
    t7 = <DataTableSearchContext.Provider value={ctx}>{t6}</DataTableSearchContext.Provider>;
    $[8] = ctx;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  return t7;
}
function subscribeToStorage(listener: () => void) {
  window.addEventListener("storage", listener);
  return () => window.removeEventListener("storage", listener);
}
export function useStoredParams(path) {
  const $ = _c(6);
  const {
    storedParamsByPath
  } = useDataTableSearchContext();
  let t0;
  if ($[0] !== path) {
    t0 = () => getSessionItem(getTableFiltersKey(path))?.trim() || null;
    $[0] = path;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const persistedQuery = useSyncExternalStore(subscribeToStorage, t0, _temp);
  let t1;
  if ($[2] !== path || $[3] !== persistedQuery || $[4] !== storedParamsByPath) {
    t1 = storedParamsByPath[path] ?? (persistedQuery ? new URLSearchParams(persistedQuery) : undefined);
    $[2] = path;
    $[3] = persistedQuery;
    $[4] = storedParamsByPath;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
}
function _temp() {
  return null;
}
export function useDataTableUrl(path) {
  const $ = _c(2);
  const searchParams = useStoredParams(path);
  if (!searchParams || searchParams.size === 0) {
    return path;
  }
  let t0;
  if ($[0] !== searchParams) {
    t0 = searchParams.toString();
    $[0] = searchParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return `${path}?${t0}`;
}
export function useDataTableFilters(path, parsers) {
  const $ = _c(6);
  const searchParams = useStoredParams(path);
  const pathname = usePathname();
  let t0;
  if ($[0] !== pathname) {
    t0 = getBasePath(pathname);
    $[0] = pathname;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const isMatchingPath = t0 === path;
  let result;
  if ($[2] !== isMatchingPath || $[3] !== parsers || $[4] !== searchParams) {
    result = {};
    for (const [key, parser] of Object.entries(parsers)) {
      const value = searchParams?.get(key);
      result[key] = isMatchingPath && value != null && typeof value === "string" ? (parser as {
        parse(queryValue: string): unknown;
      }).parse(value) : null;
    }
    $[2] = isMatchingPath;
    $[3] = parsers;
    $[4] = searchParams;
    $[5] = result;
  } else {
    result = $[5];
  }
  return result as inferParserType<Parsers>;
}